import '../../../css/core/PasswordInput.css';
import './../../../css/vendor/tables/VendorCodesInternetTable.css';

import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Row, Table } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import { SearchOutlined } from '@ant-design/icons';
import { uniqBy } from 'lodash';
import { getBooleanDisplayValue } from './../../../utils/displayUtils';
import ROUTES from '../../../constants/routes';
import COLORS from '../../../constants/colors';

import type { TVendorCodesInternetHomeWarranty } from './../../../models/vendor/VendorCodeModels';
import VendorCodeInternetHomeWarrantyModalForm from './../../vendor/forms/VendorCodeInternetHomeWarrantyModalForm';
import TableWithActionsColumn from '../../hocs/tables/TableWithActionsColumn';
import ButtonDisabledWithInsufficientPermission from '../../hocs/buttons/ButtonDisabledWithInsifficientPermission';
import TextSearchColumnFilter from '../../shared/columnFilters/TextSearchColumnFilter';

type Props = {
  data: Array<TVendorCodesInternetHomeWarranty>;
  isLoading?: boolean;
  vendorId?: number;
  allowCreate: boolean;
};

const ActionableTable = TableWithActionsColumn(Table);
const VendorCodeCreateButton = ButtonDisabledWithInsufficientPermission(Button);

const VendorCodesInternetHomeWarrantyTable = ({
  data,
  isLoading = false,
  vendorId,
  allowCreate,
}: Props) => {
  const navigate = useNavigate();
  const shouldShowVendorName = !vendorId;

  const [selectedVendorCodeInternet, setSelectedVendorCodeInternet] = useState<any>(null);
  const [isModalShown, setIsModalShown] = useState<boolean>(false);
  const [filteredDataLength, setFilteredDataLength] = useState<number>(0);

  const onUpdate = useCallback((record: TVendorCodesInternetHomeWarranty) => {
    setSelectedVendorCodeInternet(record);
    setIsModalShown(true);
  }, []);
  const columns = useMemo(
    () => getColumns(data, shouldShowVendorName, onUpdate),
    [data, onUpdate, shouldShowVendorName],
  );

  return (
    <div className="vendorCodesInternetTableRoot">
      <Row key="actionBar" justify="end">
        {allowCreate && (
          <VendorCodeCreateButton
            type="primary"
            onClick={() => {
              setSelectedVendorCodeInternet(null);
              setIsModalShown(true);
            }}
          >
            Create Vendor Code - Internet Home Warranty
          </VendorCodeCreateButton>
        )}
      </Row>
      <ActionableTable
        className="vendorMarginTopOffset20"
        bordered
        onChange={(pagination, filters, sorter, extra) => {
          setFilteredDataLength(extra.currentDataSource.length);
        }}
        columns={columns}
        dataSource={data}
        loading={isLoading}
        onRow={(record) => ({
          onDoubleClick: () =>
            navigate(`${ROUTES.VENDOR_CODES_INTERNET_HOME_WARRANTY}/${record.id}`),
        })}
        size="small"
        pagination={{
          hideOnSinglePage: false,
          responsive: true,
          total: filteredDataLength,
          showSizeChanger: true,
          defaultPageSize: 100,
          position: ['topRight', 'bottomRight'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        rowKey="id"
      />
      <VendorCodeInternetHomeWarrantyModalForm
        initialData={selectedVendorCodeInternet}
        isShown={isModalShown}
        setIsShown={setIsModalShown}
        vendorId={vendorId}
      />
    </div>
  );
};

function getColumns(
  data: Array<TVendorCodesInternetHomeWarranty>,
  shouldShowVendorName: boolean,
  onUpdate: (record: TVendorCodesInternetHomeWarranty) => void,
): Array<ColumnType<TVendorCodesInternetHomeWarranty>> {
  const columns: Array<ColumnType<TVendorCodesInternetHomeWarranty>> = [
    {
      title: 'Code',
      dataIndex: 'legacyVendorId',
      render: (value, row) => (
        <Link to={`${ROUTES.VENDOR_CODES_INTERNET_HOME_WARRANTY}/${row.id}`}>{value}</Link>
      ),
      sorter: (a, b) => a.legacyVendorId.localeCompare(b.legacyVendorId),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <TextSearchColumnFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => {
        return record.legacyVendorId
          .toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase());
      },
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? COLORS.VENDORS_SEARCH_ICON_ACTIVE : undefined }}
        />
      ),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorName',
    },
    {
      title: 'API Secret',
      dataIndex: 'secret',
      render: (value) => <Input.Password className="style-secret-disp" value={value} readOnly />,
    },
    {
      title: 'Duplicate Days',
      dataIndex: 'duplicateDays',
      sorter: (a, b) => a.duplicateDays - b.duplicateDays,
      filterSearch: true,
      filters: uniqBy(data, 'duplicateDays')
        .sort((a, b) => a.duplicateDays - b.duplicateDays)
        .map((record) => ({
          text: record.duplicateDays,
          value: record.duplicateDays,
        })),
      onFilter: (searchValue: number, record: TVendorCodesInternetHomeWarranty): boolean =>
        searchValue === record.duplicateDays,
    },
    {
      title: 'Max Home Age',
      dataIndex: 'maxHomeAge',
      sorter: (a, b) => a.maxHomeAge - b.maxHomeAge,
      filterSearch: true,
      filters: uniqBy(data, 'maxHomeAge')
        .sort((a, b) => a.maxHomeAge - b.maxHomeAge)
        .map((record) => ({
          text: record.maxHomeAge,
          value: record.maxHomeAge,
        })),
      onFilter: (searchValue: number, record: TVendorCodesInternetHomeWarranty): boolean =>
        searchValue === record.maxHomeAge,
    },
    {
      title: 'Tier',
      dataIndex: 'marketingTier',
      sorter: (a, b) => a.marketingTier - b.marketingTier,
      filterSearch: true,
      filters: uniqBy(data, 'marketingTier').map((record) => ({
        text: record.marketingTier,
        value: record.marketingTier,
      })),
      onFilter: (searchValue: number, record: TVendorCodesInternetHomeWarranty): boolean =>
        searchValue === record.marketingTier,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      render: getBooleanDisplayValue,
      sorter: (a, b) => Number(a.isActive) - Number(b.isActive),
      filterSearch: true,
      filters: [true, false].map((vciType) => ({
        text: getBooleanDisplayValue(vciType),
        value: vciType,
      })),
      onFilter: (isActive: boolean, record: TVendorCodesInternetHomeWarranty): boolean =>
        record.isActive === isActive,
      defaultFilteredValue: ['true'],
    },
    {
      title: 'Actions',
      key: 'action',
      render: (text, record) => (
        <div>
          <Button type="primary" onClick={() => onUpdate(record)}>
            Update
          </Button>
        </div>
      ),
    },
  ];

  return shouldShowVendorName ? columns : columns.filter((column) => column.title !== 'Vendor');
}

export default VendorCodesInternetHomeWarrantyTable;
